import { NodeType } from "../interface";

// ─────────────────────────────────────────────────────────────────────────────
// Interface
// ─────────────────────────────────────────────────────────────────────────────

export interface NodeBpmnMapping {
  bpmnType: string;
  isIfNode?: boolean;
  isStartEvent?: boolean;
  isEndEvent?: boolean;
  outputs?: string[];
  defaultParams?: Record<string, any>;
  description?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// NodeType.simple  →  identifié par node.service
// ─────────────────────────────────────────────────────────────────────────────

export const SIMPLE_NODE_SERVICE_MAP: Record<string, NodeBpmnMapping> = {

  // ── Start events ──────────────────────────────────────────────────────────

  EmailStartNode: {
    bpmnType: "bpmn:StartEvent",
    isStartEvent: true,
    description: "Démarre sur email entrant (IMAP). Filtre JSON Logic via `condition`.",
    defaultParams: { condition: true },
  },
  MessageStartNode: {
    bpmnType: "bpmn:StartEvent",
    isStartEvent: true,
    description: "Démarre sur message interne (Kafka/Simple). Pas de paramètre.",
    defaultParams: {},
  },
  SubcaseStartNode: {
    bpmnType: "bpmn:StartEvent",
    isStartEvent: true,
    description: "Entrée d'un sous-cas créé par SubcaseNode. Pas de paramètre.",
    defaultParams: {},
  },

  // ── End events ────────────────────────────────────────────────────────────

  EndNode: {
    bpmnType: "bpmn:EndEvent",
    isEndEvent: true,
    description: "Termine le cas. stopAll=true → terminate() ; false → soft end event.",
    defaultParams: { stopAll: false },
  },
  SubcaseEndNode: {
    bpmnType: "bpmn:EndEvent",
    isEndEvent: true,
    description: "Termine le sous-cas + renvoie dataTemplate au cas parent via replayParentToken.",
    defaultParams: { dataTemplate: {} },
  },

  // ── Gateways ──────────────────────────────────────────────────────────────

  IfNode: {
    bpmnType: "bpmn:ExclusiveGateway",
    isIfNode: true,
    outputs: ["yes", "no"],
    description: "2 outputs fixes yes/no. Paramètre condition: ContextCondition.",
    defaultParams: { condition: {} },
  },
  CaseWhenNode: {
    bpmnType: "bpmn:InclusiveGateway",
    description: "Switch/case N outputs. stopIfTrue=true s'arrête à la 1ère correspondance.",
    defaultParams: { expression: {}, stopIfTrue: true, outputs: [] },
  },
  GatewayNode: {
    // AND/XOR/OR via gatewayType. ParallelGateway = défaut (AND).
    bpmnType: "bpmn:ParallelGateway",
    description: "Gateway multi-mode AND/XOR/OR. openingNode obligatoire en mode AND.",
    defaultParams: { gatewayType: "AND", openingNode: "", expectedTokens: 1 },
  },
  DecisionRulesNode: {
    bpmnType: "bpmn:BusinessRuleTask",
    outputs: ["output"],
    description: "Évalue une liste de règles {condition,result,stop} et merge les résultats.",
    defaultParams: [],
  },
  DecisionTreeNode: {
    bpmnType: "bpmn:BusinessRuleTask",
    description: "Matrice de décision conditionMatrix×results. Chaque résultat a son output.",
    defaultParams: { conditionMatrix: [], results: [] },
  },

  // ── Attentes / timers ─────────────────────────────────────────────────────

  WaitDelayNode: {
    bpmnType: "bpmn:IntermediateCatchEvent",
    outputs: ["output"],
    description: "Suspend via token.consumeLater(date). Paramètres: duration, dayOfWeek, dayOfMonth, month, time.",
    defaultParams: { duration: { hours: 1 } },
  },
  WaitMessageNode: {
    bpmnType: "bpmn:IntermediateCatchEvent",
    outputs: ["output"],
    description: "Suspend via consumeOnMessage. Paramètres: scope (SuscriptionScope), target, data.",
    defaultParams: { scope: "case", target: "", data: {} },
  },
  StopWaitNode: {
    bpmnType: "bpmn:IntermediateThrowEvent",
    outputs: ["output"],
    description: "Débloque un WaitMessageNode. Émet stopWaiting=true sur le token attendant.",
    defaultParams: {},
  },

  // ── Messages internes ─────────────────────────────────────────────────────

  PublishMessageNode: {
    bpmnType: "bpmn:IntermediateThrowEvent",
    outputs: ["output"],
    description: "Publie un MessageType avec valueTemplate. Continue immédiatement.",
    defaultParams: { messageType: "", valueTemplate: {} },
  },

  // ── Scripts / code ────────────────────────────────────────────────────────

  JsNode: {
    bpmnType: "bpmn:ScriptTask",
    outputs: ["success", "failure"],
    description: "Script JS dans sandbox vm2 (dateFns + context). Timeout 1s.",
    defaultParams: { script: "" },
  },
  SQLNode: {
    bpmnType: "bpmn:ScriptTask",
    outputs: ["success", "failure"],
    description: "Requête SQL SELECT. Résultat dans node.context.rows.",
    defaultParams: { sql: "", connexionName: "", values: {} },
  },
  RestNode: {
    bpmnType: "bpmn:ScriptTask",
    outputs: ["success", "failure"],
    description: "Appel HTTP Axios avec templating requête/réponse. Pagination optionnelle.",
    defaultParams: { requestTemplate: { method: "GET", url: "" }, responseTemplate: {}, pagination: null },
  },

  // ── Email ─────────────────────────────────────────────────────────────────

  SendEmailNode: {
    bpmnType: "bpmn:SendTask",
    outputs: ["success", "failure"],
    description: "Email SMTP nommé. Templates Handlebars dans from/to/subject/html/text.",
    defaultParams: { senderId: "", from: "", to: "", subject: "", html: "", text: "" },
  },
  SendEmailGroupNode: {
    bpmnType: "bpmn:SendTask",
    outputs: ["success", "failure"],
    description: "Email à groupes ou listes. Résolution USER_API. Supporte cc/bcc/batchSize.",
    defaultParams: { senderId: "", from: "", to: [], cc: [], bcc: [], subject: "", html: "", batchSize: 100 },
  },

  // ── Documents ─────────────────────────────────────────────────────────────

  DocumentTemplateNode: {
    bpmnType: "bpmn:ServiceTask",
    outputs: ["success", "failure"],
    description: "Génère un document via DOCUMENT_API. addToCase attache le fichier au cas.",
    defaultParams: { code: "", data: {}, fileName: "", addToCase: false, pdf: false, merge: false, zip: false },
  },

  // ── Intégration / relations ───────────────────────────────────────────────

  // IMPORTANT : le code WFE réel est 'AutoLinkCaseNode' (voir auto-link-node-service.ts)
  AutoLinkCaseNode: {
    bpmnType: "bpmn:ServiceTask",
    outputs: ["success", "failure"],
    description: "Crée des relations entre cas. Conditions JSON Logic + filtre Prisma.",
    defaultParams: { direction: "outgoing", relationTypeCode: "", conditions: null, filterCode: null, targetCases: {} },
  },

  // ── Mises à jour du cas ───────────────────────────────────────────────────

  StatusUpdateNode: {
    bpmnType: "bpmn:ServiceTask",
    description: "Met à jour le statut du cas. Output optionnel (0 ou 1).",
    defaultParams: { status: "" },
  },
  StepsUpdateNode: {
    bpmnType: "bpmn:ServiceTask",
    description: "Met à jour les étapes prev/current/next. Output optionnel.",
    defaultParams: { steps: { prev: [], current: "", next: [] } },
  },
  OwnerUpdateNode: {
    bpmnType: "bpmn:ServiceTask",
    description: "Met à jour le propriétaire via template JSON. Output optionnel.",
    defaultParams: { owner: "" },
  },

  // ── Sous-cas ──────────────────────────────────────────────────────────────

  SubcaseNode: {
    bpmnType: "bpmn:CallActivity",
    description: "Lance un sous-cas via createSubCase(). Suspend jusqu'à done:true.",
    defaultParams: { processCode: "", nodeCode: "", inputTemplate: {} },
  },

  // ── Boucles ───────────────────────────────────────────────────────────────

  LoopNode: {
    // IMPORTANT : doit avoir une flèche de retour vers lui-même dans le diagramme
    bpmnType: "bpmn:SubProcess",
    outputs: ["start", "end"],
    description: "Itère sur itemsTemplate. Émet 'start' par item, 'end' quand liste épuisée.",
    defaultParams: { itemsTemplate: [], itemKey: "item", processCode: "", nodeCode: "", inputTemplate: {} },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// NodeType générique  →  activity / service / external
// ─────────────────────────────────────────────────────────────────────────────

export const NODE_TYPE_MAP: Partial<Record<string, NodeBpmnMapping>> = {
  // SimpleActivityNode — tâche humaine avec formulaire
  [NodeType.activity]: {
    bpmnType: "bpmn:UserTask",
    description: "Tâche humaine. Actions: submit/assign/comment/self-assign.",
    defaultParams: { isStart: false, enabledActions: ["submit", "assign", "comment"], formCode: "", owner: "", group: "" },
  },
  // SimpleServiceNode — appel async via topic
  [NodeType.service]: {
    bpmnType: "bpmn:ServiceTask",
    description: "Appel async à service externe via consumeOnMessage.",
    defaultParams: { target: "", data: {} },
  },
  [NodeType.external]: {
    bpmnType: "bpmn:Task",
    description: "Nœud externe sans logique moteur connue.",
    defaultParams: {},
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Résolution principale
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Résout le mapping BPMN d'un nœud WFE.
 * Priorité : 1. NodeType.simple + service → 2. NodeType générique
 */
export function resolveNodeBpmnMapping(
  nodeType: string,
  service: string | undefined,
): NodeBpmnMapping | null {
  if (nodeType === NodeType.simple && service) {
    const mapping = SIMPLE_NODE_SERVICE_MAP[service];
    if (mapping) return mapping;
  }
  return NODE_TYPE_MAP[nodeType] ?? null;
}
